import React, { useEffect, useState } from "react";
import { AspectRatio } from "frontend/icons/aspect-ratio";
import style from "./frameAspectRatioPicker.module.css";
import { DropdownButton } from "../toolbar-buttons";
import classNames from "classnames";
import { FramePresets, AspectRatioPreset } from "frontend/data/frames/frames-presets";

export default function AspectRatioPicker({
  value,
  onChange,
  multi,
}: {
  value?: any;
  onChange?: any;
  multi?: boolean;
}) {
  function findSelectedPreset(): AspectRatioPreset {
    if (value.scaleX !== value.scaleY) {
      return FramePresets[0];
    }
    return FramePresets.find((p) => p.width === value.width && p.height === value.height) || FramePresets[0];
  }

  const [selectedPreset, setSelectedPreset] = useState(findSelectedPreset);
  useEffect(() => {
    setSelectedPreset(findSelectedPreset);
  }, [value.width, value.height, value.scaleX, value.scaleY]);

  // Since Konva's transformer only reacts to changes in scale and not width/height,
  // setting the scale to be 1 will cause the transformer not to re-render on undoing
  // a change between 2 presets, since only the width and height change. This is solved
  // by adding a random miniscule amount to the scale. In the future it's worth
  // implementing a proper forceUpdate for the transformer on undo.
  function randomizeScale(baseScale: number) {
    const randomNumber = Math.floor(Math.random() * 10000) / 10 ** 16;
    return baseScale + randomNumber;
  }

  function handlePresetSelection(preset: AspectRatioPreset) {
    if (preset.id === selectedPreset.id) {
      return;
    }
    if (!preset.width || !preset.height) {
      setSelectedPreset(preset);
      return;
    }
    const area = value.width * (value.scaleX ?? 1) * (value.height * (value.scaleY ?? 1));
    const presetArea = preset.width * preset.height;
    const scale = randomizeScale(Math.sqrt(area / presetArea));
    onChange && onChange({ width: preset.width, height: preset.height, scaleX: scale, scaleY: scale });
  }

  function getClassName(preset: AspectRatioPreset) {
    return classNames(style.aspectRatioButton, {
      [style.selected]: preset.id === selectedPreset.id,
    });
  }

  function renderAspectRatioPresets() {
    return (
      <div className={style.aspectRatioButtonsGrid}>
        {FramePresets.map((preset) => (
          <div
            key={preset.id}
            className={getClassName(preset)}
            onClick={() => handlePresetSelection(preset)}
            data-testid={"AspectRatioButton-" + preset.id}
          >
            {preset.iconRenderer()}
            <p className={style.aspectRatioOptionLabel}>{preset.displayName}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <DropdownButton
      tooltip="Custom ratio"
      renderPopup={() => renderAspectRatioPresets()}
      data-testid="FrameAspectRatio"
      className={style.aspectRatioPopup}
      hideArrow
    >
      <AspectRatio />
      <p className={style.aspectRatioButtonLabel}>{multi ? "Multiple" : selectedPreset.displayName}</p>
    </DropdownButton>
  );
}
